//#region IMPORT
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Unstable_Grid2 as Grid } from '@mui/material';
import { RIS_CUSTOM } from './index';
import RegulationF from './Step00_Regulation/RegulationF';
import WaitF from './Step00_Wait/WaitF';
import SafetyInfoF from './Step01_SafetyInfo/SafetyInfoF';
import RiskAssessmentF from './RiskAssessment/RiskAssessmentF';
import ImprovementF from './Improvement/ImprovementF';
import { RisCustomWorkflowProvider } from './RisCustomWorkflowContext';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import {
  extractRisCustomR1RiskRule,
  useSearch1,
  useSave1,
  useDelete1,
} from './RIS_CUSTOM_QUERY';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';

import useCompanyCustDep from '../../../../hooks/useCompanyCustDep';
import useFileUpload from '../../../../hooks/useFileUpload';
import useDelete from '../../../../hooks/useDeleteN';
import HwpPreviewDialog from '../../../../Components/FilePreview/HwpPreviewDialog';
//#endregion

/**
 * 실시규정 조회 결과(RiskRule)로 모달 초기 미리보기용 파일 정보 구성
 */
const BuildRegulationInitialFileFromRule = (rule) => {
  if (!rule) return null;
  const saveName = rule?.SaveName ?? rule?.saveName ?? '';
  const originalFileName = rule?.FileName ?? rule?.fileName ?? '';
  const filePath = rule?.FilePath ?? rule?.filePath ?? '';
  if (!saveName || !filePath) return null;

  let fullFilePath = filePath;
  if (
    fullFilePath &&
    saveName &&
    !fullFilePath.endsWith(`/${saveName}`) &&
    !fullFilePath.endsWith(`\\${saveName}`)
  ) {
    fullFilePath = `${fullFilePath}/${saveName}`;
  }

  return {
    fileName: originalFileName || saveName,
    saveName,
    fullFilePath: fullFilePath.replace(/\\/g, '/'),
  };
};

//#region 상수
/** Step별 컴포넌트·props (개방-폐쇄: 새 Step 추가 시 여기만 수정) */
const STEP_CONFIG = [
  {
    id: 'regulation',
    component: RegulationF,
    getProps: (ctx) => ({
      onCancel: ctx.goToMain,
      onSaved: ctx.handleRegulationSave,
      initialMasterRow: ctx.regulationSeedRow,
    }),
  },
  {
    id: 'wait',
    component: WaitF,
    getProps: (ctx) => ({
      onNextStep: ctx.goToMain,
      onBackToList: ctx.goToMain,
      onEnterStep: ctx.handleEnterWorkflowStep,
      assessmentTitle: ctx.waitContext?.title ?? '',
      assessmentRsCd: ctx.waitContext?.rsCd ?? null,
      assessmentRsMethod: ctx.waitContext?.rsMethod ?? '',
    }),
  },
  {
    id: 'safetyInfo',
    component: SafetyInfoF,
    getProps: (ctx) => ({
      onCancel: ctx.goToWait,
      onSaved: ctx.goToWait,
      assessmentTitle: ctx.waitContext?.title ?? '',
      assessmentRsCd: ctx.waitContext?.rsCd ?? null,
    }),
  },
  {
    id: 'riskAssessment',
    component: RiskAssessmentF,
    getProps: (ctx) => ({
      onCancel: ctx.goToWait,
      onSaved: ctx.goToWait,
      assessmentTitle: ctx.waitContext?.title ?? '',
      assessmentRsCd: ctx.waitContext?.rsCd ?? null,
      assessmentRsMethod: ctx.waitContext?.rsMethod ?? '',
    }),
  },
  {
    id: 'improvement',
    component: ImprovementF,
    getProps: (ctx) => ({
      onCancel: ctx.goToWait,
      onSaved: ctx.goToWait,
      assessmentTitle: ctx.waitContext?.title ?? '',
      assessmentRsCd: ctx.waitContext?.rsCd ?? null,
    }),
  },
];
//#endregion

function RIS_CUSTOMF() {
  //#region 변수
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((s) => s.SearchInfo);

  // 회사/거래처 선택 상태 관리 및 필수 여부 검증을 위한 Hook
  const { handleCoChange, handleCustChange, validateSelection } =
    useCompanyCustDep({ searchInfo, requireCompany: true, requireCust: true });

  //#region 그리드 변수
  const gridRef1 = useRef(); // 기본 위험성평가 그리드
  const [rowData1, setRowData1] = useState([]);
  const [selectedRowData1, setSelectedRowData1] = useState([]);
  const [changedRowData1, setChangedRowData1] = useState([]);
  const [deletedRows, setDeletedRowData1] = useState([]);
  const selectedRowNosRef1 = useRef(new Set());
  const lastCheckedIndexRef1 = useRef(null);
  const selectedRowDataRef1 = useRef([]);

  // 실시규정(사업장별 1건) 상태 (등록 여부에 따라 상단 UI/버튼이 달라짐)
  const [regulationRule, setRegulationRule] = useState(null);
  const [regulationCompleted, setRegulationCompleted] = useState(false);

  // 파일 업로드 모달 상태 (HEA_BASIC_01의 FileUploadDialog와 동일 UI)
  const [fileModalToggle, setFileModalToggle] = useState(false);

  // 한글 규정 파일 미리보기 다이얼로그 상태
  const [hwpPreview, setHwpPreview] = useState(null);

  // 위험성평가 커스텀(실시규정) 첨부 파일 - FileUploadDialog용 미리보기 저장 훅
  const { SaveLogicPreview, SaveLogicFile } = useFileUpload({
    oldFilePathField: 'filePath',
    newFileNameField: 'saveName',
    getTargetPath: (item, custCd) => {
      const coCustCd = custCd || searchInfo?.custCd || '';
      // 규정은 rsCd(평가코드)와 무관하게 사업장 기준 1건으로 저장하도록 경로를 단순화
      return `RiskEvaluation/${coCustCd}/RIS_BASIC_CUSTOM/Regulation`;
    },
  });

  // 화면 전환: main(메인) | regulation(실시규정) | wait(대기화면)
  const [viewMode, setViewMode] = useState('main');
  /** 위험성평가명 클릭 시 Regulation 폼에 바인딩할 그리드 행 (신규는 null) */
  const [regulationSeedRow, setRegulationSeedRow] = useState(null);
  /** 대기(Wait) 화면에 표시/전달할 평가명·RS_CD·평가방법 */
  const [waitContext, setWaitContext] = useState({
    title: '',
    rsCd: null,
    rsMethod: '',
  });

  //#endregion

  //#region 조회
  // 메인 조회 쿼리
  const { refetch: Search1 } = useSearch1({
    onSuccess: async (rows, riskRule) => {
      setRowData1(rows ?? []);
      setDeletedRowData1([]);
      setRegulationRule(riskRule ?? null);
      setRegulationCompleted(!!riskRule);
    },
  });

  //#region 삭제·저장 mutation (ACS_BASIC_01F와 동일: useSave1 / useDelete1 + useDeleteN)
  const { runAsync: Save1Async } = useSave1();
  const delete1 = useDelete1();
  const deleteHandler1 = useDelete({
    gridRef: gridRef1,
    deleteMutation: delete1,
    updateDeleted: setDeletedRowData1,
    updateRowData: setRowData1,
    deletedRowData: deletedRows,
    rowData: rowData1,
    setChangedRowData: setChangedRowData1,
  });
  /** 삭제 예정 행 서버 반영 — useSave의 requestDelete와 동일 역할 */
  const DeleteLogic1 = deleteHandler1.DeleteLogic;
  //#endregion

  useEffect(() => {
    if (!searchInfo?.custCd) return;
    if (!validateSelection()) return;
    Search1();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInfo?.custCd, searchInfo?.startDate, searchInfo?.endDate]);

  //#endregion

  //#region 저장

  const [uploadedRegulationFile, setUploadedRegulationFile] = useState(null);

  const ResetLogic1 = () => {};

  /**
   * 모달에서 업로드한 규정 파일을 최종 저장 후 SaveRisCustomU2로 DB 등록
   */
  const SaveRegulationLogic1 = async (fileOverride = null) => {
    const uploaded = fileOverride || uploadedRegulationFile;
    if (!uploaded?.previewFilePath || !uploaded?.fileName || !uploaded?.saveName) {
      dispatch(
        showSnackbar({
          message: '저장할 파일 정보가 없습니다. 다시 업로드하세요.',
          type: 'warning',
        })
      );
      return false;
    }

    if (!searchInfo?.custCd) {
      dispatch(
        showSnackbar({
          message: '사업장코드가 없습니다. 다시 선택 후 시도하세요.',
          type: 'warning',
        })
      );
      return false;
    }

    const custCd = String(searchInfo.custCd);

    try {
      const targetDir = `RiskEvaluation/${custCd}/RIS_BASIC_CUSTOM/Regulation`;
      // DB FILE_PATH 컬럼은 대부분 "디렉토리 + 저장파일명" 형태로 저장되는 패턴이라, SAVE_NAME까지 포함한 fullPath를 전송합니다.
      const filePathForDb = `${targetDir}/${uploaded.saveName}`;

      const previousFullPath =
        BuildRegulationInitialFileFromRule(regulationRule)?.fullFilePath || '';

      // 1) PreviewFiles에 올라간 파일을 최종 경로로 이동(서버 최종 저장)
      const moveResult = await SaveLogicFile(
        {
          // oldFilePathField == 'filePath' — 수정 시 기존 파일을 치환하기 위해 전달
          filePath: previousFullPath,
          saveName: uploaded.saveName,
          previewFilePath: uploaded.previewFilePath,
        },
        custCd
      );

      if (!moveResult) {
        dispatch(
          showSnackbar({
            message: '파일 최종 저장에 실패했습니다.',
            type: 'error',
          })
        );
        return false;
      }

      // 2) DB에 규정 등록(INSERT/UPDATE) — useSave1 → saveRisCustomU2
      const result = await Save1Async([
        {
          regStatus: 'Y',
          fileName: uploaded.fileName || '',
          filePath: filePathForDb,
          saveName: uploaded.saveName || '',
        },
      ]);

      const isSuccess = result?.IsSuccess ?? result?.isSuccess;
      const errMsg = result?.Message ?? result?.message;

      if (!isSuccess) {
        console.log('SaveRisCustomU2 response:', result);
        dispatch(
          showSnackbar({
            message: errMsg || '규정 저장 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
        return false;
      }

      dispatch(
        showSnackbar({
          message: '규정이 저장되었습니다.',
          type: 'success',
        })
      );

      setUploadedRegulationFile(null);
      await Search1();
      return true;
    } catch (e) {
      console.error(e);
      dispatch(
        showSnackbar({
          message: e?.message || '규정 저장 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
      return false;
    }
  };

  /**
   * 등록된 실시규정 파일 다운로드(=미리보기 버튼 대체)
   * - 서버에서 내려준 (FilePath, SaveName, FileName) 조합으로 최종 다운로드 URL 구성
   */
  const handlePreviewRegulation = async () => {
    const tryOpenUrl = (url) => {
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // 팝업 차단이면 현재 페이지는 유지하고 안내만 띄웁니다.
        // dispatch(
        //   showSnackbar({
        //     message: '새 탭을 열 수 없습니다. 팝업 차단을 해제해 주세요.',
        //     type: 'warning',
        //   })
        // );
        return false;
      }
      return true;
    };

    const buildFileUrlFromRule = (rule) => {
      const saveName = rule?.SaveName ?? rule?.saveName ?? '';
      const originalFileName = rule?.FileName ?? rule?.fileName ?? '';
      const filePath = rule?.FilePath ?? rule?.filePath ?? '';

      if (!saveName || !filePath) return null;

      // FilePath가 디렉토리일 수도 있고, 파일명을 포함할 수도 있어 보정
      let fullFilePath = filePath || '';
      if (
        fullFilePath &&
        saveName &&
        !fullFilePath.endsWith(`/${saveName}`) &&
        !fullFilePath.endsWith(`\\${saveName}`)
      ) {
        fullFilePath = `${fullFilePath}/${saveName}`;
      }

      const normalizedFilePath = (fullFilePath || '')
        .replace(`${Global.file.useUrl}files/`, '')
        .replace(/^\/?files\//, '');

      const fileUrl = `${Global.file.useUrl}files/${Commons.EncodeUrl(
        normalizedFilePath
      )}`;

      const finalFileName = originalFileName || saveName || '';

      return {
        fileUrl,
        finalFileName,
        // 한글은 브라우저가 렌더링하지 못하므로 rhwp 뷰어 다이얼로그로 연다.
        isHwp: /\.(hwp|hwpx)$/i.test(normalizedFilePath),
        filePath: normalizedFilePath,
      };
    };

    const openRule = (info) => {
      if (info.isHwp) {
        setHwpPreview({
          filePath: info.filePath,
          fileName: info.finalFileName,
        });
        return;
      }
      tryOpenUrl(info.fileUrl);
    };

    // 1) 먼저 현재 state(regulationRule) 기준으로 바로 새 탭 시도
    // (async 중간 이후 window.open은 팝업 차단에 걸릴 수 있어서)
    const initial = buildFileUrlFromRule(regulationRule);
    if (initial?.fileUrl) {
      openRule(initial);
      return;
    }

    // 2) 파일이 비어있으면 미리보기 클릭 시점에 최신 서버로 한 번 더 조회 후 재시도
    try {
      const refetchResult = await Search1?.();
      const latestRule = extractRisCustomR1RiskRule(
        refetchResult?.data ?? null
      );
      const rule = latestRule ?? regulationRule;

      const latest = buildFileUrlFromRule(rule);
      if (!latest?.fileUrl) {
        dispatch(
          showSnackbar({
            message: '등록된 실시규정 파일이 없습니다.',
            type: 'warning',
          })
        );
        return;
      }

      openRule(latest);
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '규정 파일 조회 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  const NewYnErrorMsg = () => {
    dispatch(
      showSnackbar({
        message: '데이터 입력중입니다. 저장 후 시도하세요.',
        type: 'warning',
      })
    );
  };

  //#endregion
  const handleRowSelection1 = (selectedRows) => {
    selectedRowDataRef1.current = selectedRows;
    setSelectedRowData1(selectedRows);
  };

  const handleRegulationFileUploaded = (fileInfo) => {
    setUploadedRegulationFile(fileInfo || null);
  };

  /**
   * "규정 등록하기" / "규정 수정하기" -> 파일업로드 모달 오픈
   * - 그리드 행(row)과 무관하게 오픈 (rowless 업로드)
   */
  const regulationInitialFile = useMemo(
    () =>
      regulationCompleted && regulationRule
        ? BuildRegulationInitialFileFromRule(regulationRule)
        : null,
    [regulationCompleted, regulationRule]
  );

  const handleOpenRegulationFileUpload = () => {
    setFileModalToggle(true);
  };

  const goToMain = () => {
    setRegulationSeedRow(null);
    setViewMode('main');
  };
  const goToWait = () => setViewMode('wait');

  const HandleEnterWorkflowStep = (stepId, context = {}) => {
    const nextRsCd =
      context?.rsCd != null && context?.rsCd !== ''
        ? Number(context.rsCd)
        : Number(waitContext?.rsCd);
    const nextTitle = context?.title || waitContext?.title || '';
    const nextRsMethod = context?.rsMethod || waitContext?.rsMethod || '';
    if (Number.isFinite(nextRsCd) && nextRsCd > 0) {
      setWaitContext({ title: nextTitle, rsCd: nextRsCd, rsMethod: nextRsMethod });
    }
    if (stepId === 'safetyInfo') {
      setViewMode('safetyInfo');
      return;
    }
    if (stepId === 'riskAssessment') {
      setViewMode('riskAssessment');
      return;
    }
    if (stepId === 'improvement') {
      setViewMode('improvement');
      return;
    }
    dispatch(
      showSnackbar({
        message: `「${stepId}」 단계 화면은 추후 연결 예정입니다.`,
        type: 'info',
      })
    );
  };

  /** 그리드 '위험성평가 실시' → 이동: Step00_Wait 대기화면 */
  const HandleExecuteRiskAssessment = async (rowData) => {
    if (!rowData) return;
    const rsCd = rowData.rsCd ?? rowData.RsCd;
    if (rsCd == null || rsCd === '') {
      dispatch(
        showSnackbar({
          message: '위험성평가 코드(RS_CD)가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }
    setWaitContext({
      title: rowData.rsNm ?? rowData.RsNm ?? '',
      rsCd,
      rsMethod: rowData.rsMethod ?? rowData.RsMethod ?? '',
    });
    goToWait();
  };

  /** 메인 그리드에서 위험성평가명 클릭 → Regulation 폼에 행 데이터 바인딩 */
  const handleOpenRegulationFromRow = (row) => {
    setRegulationSeedRow(row ?? null);
    setViewMode('regulation');
  };

  // +(추가) 클릭 시 실시규정 화면으로 (신규: 시드 없음)
  const handleAddClick = () => {
    setRegulationSeedRow(null);
    setViewMode('regulation');
  };

  // 실시규정(기본정보) 저장 후 메인 그리드(목록)로 복귀 + 재조회
  const handleRegulationSave = async () => {
    goToMain();
    try {
      await Search1();
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * 툴바 저장(HEA의 SaveLogic1 패턴) — 삭제 예정 행 처리 후 규정 파일 저장
   */
  const SaveLogic1 = async () => {
    if (!validateSelection()) return;

    const hadDeletes = deletedRows.length > 0;
    if (hadDeletes) {
      try {
        const res = await DeleteLogic1();
        if (res && res.isSuccess === false) {
          dispatch(
            showSnackbar({
              message:
                res?.message ||
                res?.Message ||
                '삭제 처리 중 오류가 발생했습니다.',
              type: 'error',
            })
          );
          await Search1();
          return;
        }
        dispatch(
          showSnackbar({
            message: '삭제되었습니다.',
            type: 'success',
          })
        );
      } catch (e) {
        dispatch(
          showSnackbar({
            message: e?.message || '삭제 처리 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
        await Search1();
        return;
      }
      await Search1();
      selectedRowNosRef1.current = new Set();
      lastCheckedIndexRef1.current = null;
      setSelectedRowData1([]);
      gridRef1.current?.api?.deselectAll();
    }

    const hasRegulationUpload =
      !!uploadedRegulationFile?.previewFilePath &&
      !!uploadedRegulationFile?.saveName;
    if (hasRegulationUpload) {
      await SaveRegulationLogic1();
    } else if (!hadDeletes) {
      dispatch(
        showSnackbar({
          message: '저장할 내용이 없습니다.',
          type: 'info',
        })
      );
    }
  };

  const stepContext = {
    goToMain,
    goToWait,
    handleRegulationSave,
    regulationSeedRow,
    waitContext,
    handleEnterWorkflowStep: HandleEnterWorkflowStep,
  };

  const step = STEP_CONFIG.find((s) => s.id === viewMode);
  if (step) {
    const StepComponent = step.component;
    const stepProps = step.getProps(stepContext);
    return (
      <RisCustomWorkflowProvider waitRsCd={waitContext?.rsCd}>
        <div className="wrap">
          <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
            <Grid xs={4} md={12}>
              <StepComponent {...stepProps} />
            </Grid>
          </Grid>
        </div>
      </RisCustomWorkflowProvider>
    );
  }

  return (
    <RisCustomWorkflowProvider waitRsCd={waitContext?.rsCd}>
      <div className="wrap">
      <RIS_CUSTOM
        gridId1={'grid1'}
        userInfo={userInfo}
        rowData1={rowData1}
        selectedRowData1={selectedRowData1}
        setSelectedRowData1={setSelectedRowData1}
        changedRowData1={changedRowData1}
        setChangedRowData1={setChangedRowData1}
        selectedRowNosRef1={selectedRowNosRef1}
        lastCheckedIndexRef1={lastCheckedIndexRef1}
        gridRef1={gridRef1}
        ResetLogic1={ResetLogic1}
        SaveLogic1={SaveLogic1}
        SaveRegulationLogic1={SaveRegulationLogic1}
        NewYnErrorMsg={NewYnErrorMsg}
        handleRowSelection1={handleRowSelection1}
        handleCoChange={handleCoChange}
        handleCustChange={handleCustChange}
        validateSelection={validateSelection}
        dispatchGrid={dispatch}
        onAddClick={handleAddClick}
        onAddRegulation={handleOpenRegulationFileUpload}
        onPreviewRegulation={handlePreviewRegulation}
        onEditRegulation={handleOpenRegulationFileUpload}
        onOpenRegulation={handleOpenRegulationFromRow}
        onExecuteRiskAssessment={HandleExecuteRiskAssessment}
        regulationCompleted={regulationCompleted}
        MarkedForDeletion1={deleteHandler1.MarkedForDeletion}
        RestoreRow1={deleteHandler1.RestoreRow}
        DeleteNewRow1={deleteHandler1.DeleteNewRow}
        fileModalToggle={fileModalToggle}
        setFileModalToggle={setFileModalToggle}
        SaveLogicPreview={SaveLogicPreview}
        onUploadedRegulationFile={handleRegulationFileUploaded}
        initialExistingFile={regulationInitialFile}
      />
      <HwpPreviewDialog
        open={!!hwpPreview}
        filePath={hwpPreview?.filePath}
        fileName={hwpPreview?.fileName}
        onClose={() => setHwpPreview(null)}
      />
      </div>
    </RisCustomWorkflowProvider>
  );
}

export default RIS_CUSTOMF;
